import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import Header from "../../layouts/Header";
import "../../assets/styles/local/Notifications.css";
import mailIcon from "../../assets/icons/mail.svg";
import bookmarkIcon from "../../assets/icons/bookmark.svg";
import bookmarkFilled from "../../assets/icons/bookmarkFilled.svg";
import deleteIcon from "../../assets/icons/Bin.svg";
import deleteFilled from "../../assets/icons/deleteFilled.svg";
import settingsIcon from "../../assets/icons/settings.svg";
import filtersIcon from "../../assets/icons/filtersIcon.svg";
import chatBackIcon from "../../assets/icons/chat Back.png";
import closeIcon from "../../assets/icons/x-circle.svg";
import arrowDownIcon from "../../assets/icons/arrowdown.svg";
import commentIcon from "../../assets/icons/Buble speech.svg";
import userAvatar from "../../assets/images/Anna.png";
import { axiosInstance } from "../../redux/constants/axiosInstance";
import { CHAT_API_URL } from "../../redux/constants/type";
import { handleGeneralNotification } from "../../utils/helpers/notifications";
import { resolveNotificationAvatarUrl } from "../../utils/helpers/notificationAvatar";
import {
  getUserNotifications,
  markAsReadNotification,
  archiveNotification,
  bookmarkNotification,
  fetchArchivedNotifications,
  fetchBookmarkedNotifications,
  unarchiveNotification,
  unbookmarkNotification,
} from "../../redux/actions/action";
import { fetchScreenTranslations } from "../../utils/helpers/translations";

const userEmail = localStorage.getItem("userEmail");

const tabs = ["Activity", "Bookmarks", "Archive"];
const notificationFilters = [
  { key: "all", label: "All" },
  { key: "forme", label: "For me" },
  { key: "fromperson", label: "From person", hasSubmenu: true },
  { key: "fromproject", label: "From project", hasSubmenu: true },
  { key: "assignedtome", label: "Assigned to me" },
  { key: "mentioned", label: "@Mentioned" },
  { key: "assignedbyme", label: "Assigned by me" },
  { key: "unread", label: "Unread only" },
];

const browserNotifications = [
  {
    title: "All activity",
    description:
      "You're assigned a task, you are @mentioned, something other co-workers mention in the task",
  },
  { title: "Mentions", description: "Only when you are @mentioned" },
  { title: "Nothing" },
];

const emailNotificationGroups = [
  {
    title: "Updates on your work",
    items: [
      "When task is shared with you",
      "When task is assigned to you",
      "When due date changes on task assigned to you",
      "When task assigned to you is completed",
      "When your task is blocked or unblocked",
    ],
  },
  {
    title: "Communication",
    items: [
      "Someone mentions @Luna Wonder",
      "When comments or attachments are sent",
      "When status updates are posted",
      "When messages are sent",
    ],
  },
  {
    title: "Summaries",
    items: ["Daily tasks assigned to you", "Weekly overdue tasks"],
  },
  {
    title: "Other",
    items: [
      "Reminders to finish drafted comments",
      "Updates about users you've invited",
    ],
  },
];

const pauseNotificationOptions = [
  "30 minutes",
  "1 hour",
  "2 hours",
  "4 hours",
  "8 hours",
  "Until tomorrow",
];

const DEFAULT_SETTINGS_DRAFT = {
  browserMode: "All activity",
  emailSelection: buildDefaultEmailSelection(),
  preferredEmail: userEmail,
  dndEnabled: false,
  dndPauseOption: "",
  dndStartTime: "09:00",
  dndEndTime: "17:00",
  // dndDays: ["Mon", "Tue", "Wed"],
  dndDays: ["Sat", "Sun"],
};

const Notifications = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const filterRef = useRef(null);
  const userEmail = localStorage.getItem("userEmail");
  const userID = useMemo(() => window.localStorage.getItem("userid"), []);
  const userNotificationList = useSelector(
    (state) => state.user.userNotifications,
  );

  const [fetchTransUpdateTaskList, setFetchTransUpdateTaskList] = useState({});

  useEffect(() => {
    fetchScreenTranslations("Notification").then((res) => {
      setFetchTransUpdateTaskList(res);
    });
  }, []);

  const t = (key) => fetchTransUpdateTaskList?.[key] || "";

  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState("Activity");
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilter, setActiveFilter] = useState({
    key: "all",
    label: "All",
    value: "",
  });
  const [activeFilterMenu, setActiveFilterMenu] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [activeActionRowId, setActiveActionRowId] = useState(null);
  const [bookmarkedNotifications, setBookmarkedNotifications] = useState([]);
  const [archivedNotifications, setArchivedNotifications] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [tabError, setTabError] = useState("");

  const activityFilterRequest = useMemo(
    () => buildNotificationFilterRequest(activeFilter),
    [activeFilter],
  );

  const activityFetchKey = useMemo(
    () =>
      activeTab === "Activity"
        ? JSON.stringify(activityFilterRequest)
        : activeTab,
    [activeTab, activityFilterRequest],
  );

  useEffect(() => {
    if (!userID) {
      return;
    }

    const loadTabNotifications = async () => {
      setTabLoading(true);
      setTabError("");

      try {
        if (activeTab === "Bookmarks") {
          const response = await fetchBookmarkedNotifications(userID);
          setBookmarkedNotifications(
            normalizeNotificationList(response?.data ?? response ?? []),
          );
        } else if (activeTab === "Archive") {
          const response = await fetchArchivedNotifications(userID);
          setArchivedNotifications(
            normalizeNotificationList(response?.data ?? response ?? []),
          );
        } else {
          await dispatch(getUserNotifications(userID, activityFilterRequest));
        }
      } catch (error) {
        setTabError("Unable to load notifications.");
      } finally {
        setTabLoading(false);
      }
    };

    loadTabNotifications();
  }, [activityFetchKey, dispatch, userID]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilters(false);
        setActiveFilterMenu(null);
      }
      if (!event.target.closest(".notification-row")) {
        setActiveActionRowId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizedActivityNotifications = useMemo(
    () => normalizeNotificationList(userNotificationList),
    [userNotificationList],
  );

  const activityFilterCatalog = useMemo(
    () => buildFilterCatalog(normalizedActivityNotifications),
    [normalizedActivityNotifications],
  );

  const currentNotifications = useMemo(() => {
    if (activeTab === "Bookmarks") {
      return bookmarkedNotifications;
    }

    if (activeTab === "Archive") {
      return archivedNotifications;
    }

    return normalizedActivityNotifications.filter(
      (item) => !item.isArchived && !item.isBookmarked,
    );
  }, [
    activeTab,
    archivedNotifications,
    bookmarkedNotifications,
    normalizedActivityNotifications,
  ]);

  const filteredNotifications = useMemo(() => {
    const trimmedQuery = query.trim().toLowerCase();
    if (!trimmedQuery) {
      return currentNotifications;
    }

    return currentNotifications.filter((item) => {
      const haystack = [
        item.title,
        item.description,
        item.projectName,
        item.senderName,
        item.actionText,
        item.message,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(trimmedQuery);
    });
  }, [currentNotifications, query]);

  const groupedNotifications = useMemo(
    () => groupNotificationsByDate(filteredNotifications),
    [filteredNotifications],
  );

  const handleFilterSelect = (filter) => {
    setActiveFilter(filter);
    setShowFilters(false);
    setActiveFilterMenu(null);
  };

  const handleFilterMenuOpen = (menuKey) => {
    setActiveFilterMenu((prev) => (prev === menuKey ? null : menuKey));
  };

  const handleSubFilterSelect = (filterKey, item) => {
    const label = item?.label || item?.name || item?.title || "Selected";
    setActiveFilter({
      key: filterKey,
      label,
      value: item?.id || item?.value || "",
    });
    setShowFilters(false);
    setActiveFilterMenu(null);
  };

  const openNotificationTarget = (item) => {
    const data = item?.data ? safeParseNotificationData(item.data) : null;
    if (!data) {
      // return;
    }

    switch (item.notificationType) {
      case "CHAT_ADDED":
      case "CHAT_ADMIN":
      case "CHAT_MESSAGE":
        if (item?.groupId) {
          // history.push(`/chat?room=${data.groupId}`);
          history.push(`/chat?room=${item?.groupId}`);
        }
        break;
      case "TEAM_JOIN":
      case "UPDATE_PROFILE":
        if (item?.userId) {
          // history.push(`/updat-member/${data?.userId}`);
          history.push(`/updat-member/${item?.userId}`);
        }
        break;
      case "TASK_ASSIGN":
      case "TASK_DUE_DATE_CHANGE":
      case "TASK_DUE_TIME_CHANGE":
      case "TASK_STATUS_CHANGE":
      case "TASK_DUE":
      case "COMMENT_TAGGED":
        if (data.taskId) {
          window.localStorage.setItem("taskid", data.taskId);
          if (window.location.pathname === "/planboard") {
            window.location.reload();
          } else {
            history.push("/planboard");
          }
        }
        break;
      case "USER_BIRTHDAY":
        if (data.groupId) {
          history.push(
            `/chat?room=${data.groupId || "new"}&userid=${data.userId}`,
          );
        } else {
          handleGeneralNotification({
            notificationType: "CUSTOM",
            text: "User not created! Please check the user details.",
          });
        }
        break;
      case "QUOTE_SIGNED":
        if (data.quoteLink) {
          window.open(data.quoteLink, "_blank");
        }
        break;
      default:
        break;
    }
  };

  const handleNotificationClick = (item) => {
    if (!item?.id) {
      return;
    }

    setActiveActionRowId(item.id);
    if (!item.isSeen) {
      dispatch(markAsReadNotification(item.id, userID));
    }

    openNotificationTarget(item);
  };

  const refreshCurrentTab = async () => {
    if (!userID) {
      return;
    }

    if (activeTab === "Bookmarks") {
      const response = await fetchBookmarkedNotifications(userID);
      setBookmarkedNotifications(
        normalizeNotificationList(response?.data ?? response ?? []),
      );
      return;
    }

    if (activeTab === "Archive") {
      const response = await fetchArchivedNotifications(userID);
      setArchivedNotifications(
        normalizeNotificationList(response?.data ?? response ?? []),
      );
      return;
    }

    await dispatch(getUserNotifications(userID, activityFilterRequest));
  };

  const handleBookmarkToggle = async (item) => {
    if (!item?.id) {
      return;
    }

    setActiveActionRowId(item.id);
    if (item.isBookmarked) {
      await unbookmarkNotification(item.id);
    } else {
      await bookmarkNotification(item.id);
    }

    await refreshCurrentTab();
  };

  const handleArchiveToggle = async (item) => {
    if (!item?.id) {
      return;
    }

    setActiveActionRowId(item.id);
    await archiveNotification(item.id);
    await refreshCurrentTab();
  };

  const handleUnarchive = async (item) => {
    if (!item?.id) {
      return;
    }

    setActiveActionRowId(item.id);
    await unarchiveNotification(item.id);
    await refreshCurrentTab();
  };

  const handleSettingsClose = () => {
    setShowSettings(false);
  };

  const renderNotifications = (notifications) =>
    notifications.map((item) => (
      <NotificationRow
        key={item.id}
        item={item}
        isActionsActive={activeActionRowId === item.id}
        onClick={() => handleNotificationClick(item)}
        onBookmarkToggle={() => handleBookmarkToggle(item)}
        onArchiveToggle={() => handleArchiveToggle(item)}
        onUnarchive={() => handleUnarchive(item)}
        activeTab={activeTab}
      />
    ));

  return (
    <>
      <Header
        setQuery={setQuery}
        leftComponent={
          <div className="notifications-page-header">
            <button
              className="notifications-back-button"
              type="button"
              onClick={() => history.goBack()}
              aria-label="Go back"
            >
              <img src={chatBackIcon} alt="" />
            </button>
            <div className="notifications-page-title">{t("Notifications")}</div>
            <div className="notifications-filter-wrap" ref={filterRef}>
              <button
                className="notifications-filter-button"
                type="button"
                onClick={() => {
                  setShowFilters((prev) => !prev);
                  setActiveFilterMenu(null);
                }}
              >
                <span>{t("Filters")}</span>
                <img src={filtersIcon} alt="" />
              </button>
              {showFilters && (
                <div className="notifications-filter-popup">
                  <button
                    className="notifications-filter-close"
                    type="button"
                    onClick={() => setShowFilters(false)}
                    aria-label="Close filters"
                  >
                    <img src={closeIcon} alt="" />
                  </button>
                  {notificationFilters.map((filter) => {
                    const isActive = activeFilter.key === filter.key;
                    return (
                      <div
                        className="notifications-filter-row"
                        key={filter.key}
                      >
                        <button
                          className={`notifications-filter-option ${
                            isActive ? "notifications-filter-option-active" : ""
                          }`}
                          type="button"
                          aria-pressed={isActive}
                          onClick={() =>
                            filter.hasSubmenu
                              ? handleFilterMenuOpen(filter.key)
                              : handleFilterSelect(filter)
                          }
                        >
                          <span className="notifications-filter-radio" />
                          {/* <span>{filter.label}</span> */}
                          <span>{t(filter.label) || filter.label}</span>
                          {filter.hasSubmenu && (
                            <span className="notifications-filter-arrow">
                              {">"}
                            </span>
                          )}
                        </button>
                        {filter.hasSubmenu &&
                          activeFilterMenu === filter.key && (
                            <div className="notifications-filter-submenu">
                              {(filter.key === "fromperson"
                                ? activityFilterCatalog.personOptions
                                : activityFilterCatalog.projectOptions
                              ).length > 0 ? (
                                (filter.key === "fromperson"
                                  ? activityFilterCatalog.personOptions
                                  : activityFilterCatalog.projectOptions
                                ).map((option) => (
                                  <button
                                    type="button"
                                    key={option.id}
                                    className="notifications-filter-submenu-option"
                                    onClick={() =>
                                      handleSubFilterSelect(filter.key, option)
                                    }
                                  >
                                    <span>{option.label}</span>
                                  </button>
                                ))
                              ) : (
                                <div className="notifications-filter-submenu-empty">
                                  No {filter.label.toLowerCase()} filters found.
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        }
      />
      <div className="notifications-page">
        <div className="notifications-topbar">
          <div className="notifications-tabs" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab}
                className={`notifications-tab ${
                  activeTab === tab ? "notifications-tab-active" : ""
                }`}
                type="button"
                onClick={() => setActiveTab(tab)}
              >
                {/* {tab} */}
                {t(tab)}
              </button>
            ))}
          </div>
          <button
            className="notifications-manage"
            type="button"
            onClick={() => setShowSettings(true)}
          >
            <span>{t("Manage notifications")}</span>
            <img src={settingsIcon} alt="" />
          </button>
        </div>

        <div className="notifications-list">
          {tabError && (
            <div className="notifications-empty-state">{tabError}</div>
          )}

          {tabLoading ? (
            <div className="notifications-empty-state">
              {/* Loading notifications... */}
              {t("Loading notifications...")}
            </div>
          ) : groupedNotifications.length > 0 ? (
            groupedNotifications.map((section) => (
              <section className="notifications-section" key={section.label}>
                <div className="notifications-day-label">{section.label}</div>
                {renderNotifications(section.items)}
              </section>
            ))
          ) : (
            <div className="notifications-empty-state">
              No {activeTab.toLowerCase()} notifications found.
            </div>
          )}
        </div>
      </div>

      {showSettings && (
        <NotificationSettingsModal
          userID={userID}
          onClose={handleSettingsClose}
          t={t}
        />
      )}
    </>
  );
};

const NotificationSettingsModal = ({ userID, onClose, t }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [settingsSource, setSettingsSource] = useState(null);
  const [draft, setDraft] = useState(DEFAULT_SETTINGS_DRAFT);
  const [openSettingPanels, setOpenSettingPanels] = useState({
    browser: true,
    email: true,
    dnd: true,
  });

  useEffect(() => {
    let isMounted = true;

    const loadSettings = async () => {
      if (!userID) {
        return;
      }

      setIsLoading(true);
      setError("");
      try {
        const response = await axiosInstance.get(
          `${CHAT_API_URL}/api/notification-settings/${userID}`,
        );
        const normalized = normalizeSettingsResponse(response?.data);
        if (!isMounted) {
          return;
        }
        setSettingsSource(normalized.raw);
        setDraft((prev) => ({
          ...prev,
          ...normalized.draft,
          emailSelection: {
            ...buildDefaultEmailSelection(),
            ...normalized.draft.emailSelection,
          },
        }));
      } catch (requestError) {
        if (!isMounted) {
          return;
        }
        setError("Unable to load notification settings.");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, [userID]);

  const handleToggleEmailSelection = (label) => {
    setDraft((prev) => ({
      ...prev,
      emailSelection: {
        ...prev.emailSelection,
        [label]: !prev.emailSelection[label],
      },
    }));
  };

  const toggleSettingPanel = (panelKey) => {
    setOpenSettingPanels((prev) => ({
      ...prev,
      [panelKey]: !prev[panelKey],
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError("");
    try {
      const payload = buildSettingsPayload(settingsSource, draft);
      await axiosInstance.post(
        `${CHAT_API_URL}/api/notification-settings`,
        payload,
      );
      onClose();
    } catch (requestError) {
      setError("Unable to save notification settings.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="notification-settings-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="notification-settings-modal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="notification-settings-title-row">
          <h2>{t("Notifications settings")}</h2>
          <button
            className="notification-settings-close"
            type="button"
            onClick={onClose}
            aria-label="Close notification settings"
          >
            <img src={closeIcon} alt="" />
          </button>
        </div>

        <div className="notification-settings-content">
          {error && <div className="notification-settings-error">{error}</div>}

          {isLoading ? (
            <div className="notification-settings-loading">
              Loading notification settings...
            </div>
          ) : (
            <>
              <section className="notification-settings-panel">
                <button
                  className="notification-settings-panel-header"
                  type="button"
                  aria-expanded={openSettingPanels.browser}
                  onClick={() => toggleSettingPanel("browser")}
                >
                  <span>{t("Browser notifications")}</span>
                  <img
                    className={
                      openSettingPanels.browser
                        ? "notification-settings-arrow-open"
                        : ""
                    }
                    src={arrowDownIcon}
                    alt=""
                  />
                </button>
                {openSettingPanels.browser && (
                  <div className="notification-settings-panel-body">
                    <BrowserNotificationSettings
                      value={draft.browserMode}
                      onChange={(browserMode) =>
                        setDraft((prev) => ({ ...prev, browserMode }))
                      }
                      t={t}
                    />
                  </div>
                )}
              </section>

              <section className="notification-settings-panel">
                <button
                  className="notification-settings-panel-header"
                  type="button"
                  aria-expanded={openSettingPanels.email}
                  onClick={() => toggleSettingPanel("email")}
                >
                  <span>{t("Email notifications")}</span>
                  <img
                    className={
                      openSettingPanels.email
                        ? "notification-settings-arrow-open"
                        : ""
                    }
                    src={arrowDownIcon}
                    alt=""
                  />
                </button>
                {openSettingPanels.email && (
                  <div className="notification-settings-panel-body">
                    <EmailNotificationSettings
                      emailSelection={draft.emailSelection}
                      preferredEmail={draft.preferredEmail}
                      onToggle={handleToggleEmailSelection}
                      onEmailChange={(preferredEmail) =>
                        setDraft((prev) => ({ ...prev, preferredEmail }))
                      }
                      t={t}
                    />
                  </div>
                )}
              </section>

              <section className="notification-settings-panel">
                <button
                  className="notification-settings-panel-header"
                  type="button"
                  aria-expanded={openSettingPanels.dnd}
                  onClick={() => toggleSettingPanel("dnd")}
                >
                  <span>{t("Do not disturb")}</span>
                  <img
                    className={
                      openSettingPanels.dnd
                        ? "notification-settings-arrow-open"
                        : ""
                    }
                    src={arrowDownIcon}
                    alt=""
                  />
                </button>
                {openSettingPanels.dnd && (
                  <div className="notification-settings-panel-body">
                    <DoNotDisturbSettings
                      value={draft}
                      onChange={(updates) =>
                        setDraft((prev) => ({ ...prev, ...updates }))
                      }
                      t={t}
                    />
                  </div>
                )}
              </section>
            </>
          )}
        </div>

        <div className="notification-settings-footer">
          <button
            className="notification-settings-secondary"
            type="button"
            onClick={onClose}
          >
            {t("Cancel")}
          </button>
          <button
            className="notification-settings-primary"
            type="button"
            onClick={handleSave}
            disabled={isLoading || isSaving}
          >
            {/* {isSaving ? "Saving..." : "Save changes"} */}
            {isSaving ? t("Saving...") : t("Save changes")}
          </button>
        </div>
      </div>
    </div>
  );
};

const BrowserNotificationSettings = ({ value, onChange, t }) => {
  return (
    <>
      <p className="notification-settings-helper">
        {t("Customize what notifications you receive in your browser")}
      </p>
      <div className="notification-settings-options">
        {browserNotifications.map((item) => {
          const isActive = value === item.title;

          return (
            <label className="notification-settings-radio-row" key={item.title}>
              <input
                type="radio"
                name="browser-notification-setting"
                checked={isActive}
                onChange={() => onChange(item.title)}
              />
              <span
                className={`notification-settings-radio ${
                  isActive ? "notification-settings-radio-active" : ""
                }`}
              />
              <span>
                {/* <strong>{item.title}</strong> */}
                <strong>{t(item.title) || item.title}</strong>
                {item.description && <small>{t(item.description)}</small>}
              </span>
            </label>
          );
        })}
      </div>
    </>
  );
};

const EmailNotificationSettings = ({
  emailSelection,
  preferredEmail,
  onToggle,
  onEmailChange,
  t,
}) => {
  return (
    <div className="notification-settings-email">
      {emailNotificationGroups.map((group) => (
        <div className="notification-settings-email-group" key={group.title}>
          <label className="notification-settings-check-row notification-settings-group-title">
            <input
              type="checkbox"
              checked={group.items.every((item) => emailSelection[item])}
              onChange={() => {
                const nextValue = !group.items.every(
                  (item) => emailSelection[item],
                );
                group.items.forEach((item) => {
                  if (emailSelection[item] !== nextValue) {
                    onToggle(item);
                  }
                });
              }}
            />
            <span
              className={`notification-settings-check ${
                group.items.every((item) => emailSelection[item])
                  ? "notification-settings-check-active"
                  : ""
              }`}
            />
            {/* <strong>{group.title}</strong> */}
            <strong>{t(group.title) || group.title}</strong>
          </label>
          <div className="notification-settings-email-items">
            {group.items.map((item) => {
              const isChecked = Boolean(emailSelection[item]);

              return (
                <label className="notification-settings-check-row" key={item}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => onToggle(item)}
                  />
                  <span
                    className={`notification-settings-check ${
                      isChecked ? "notification-settings-check-active" : ""
                    }`}
                  />
                  {/* <span>{item}</span> */}
                  <span>{t(item)}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
      <label className="notification-settings-email-label">
        {t("Preferred notification email")}
        <input
          type="email"
          value={preferredEmail}
          onChange={(event) => onEmailChange(event.target.value)}
          placeholder="name@example.com"
        />
      </label>
    </div>
  );
};

const DoNotDisturbSettings = ({ value, onChange, t }) => {
  const [showPauseOptions, setShowPauseOptions] = useState(false);

  const toggleDay = (day) => {
    const nextDays = value.dndDays.includes(day)
      ? value.dndDays.filter((selectedDay) => selectedDay !== day)
      : [...value.dndDays, day];
    onChange({ dndDays: nextDays });
  };

  return (
    <div className="notification-settings-dnd">
      <div className="notification-settings-pause-wrap">
        <button
          className="notification-settings-pause"
          type="button"
          onClick={() => setShowPauseOptions((prev) => !prev)}
        >
          {value.dndPauseOption || t("Pause notifications")}
        </button>
        {showPauseOptions && (
          <div className="notification-settings-pause-menu">
            {pauseNotificationOptions.map((option) => (
              <button
                type="button"
                key={option}
                onClick={() => {
                  onChange({ dndPauseOption: option, dndEnabled: true });
                  setShowPauseOptions(false);
                }}
              >
                <span>{option}</span>
                {option === "Until tomorrow" && <small>9 am</small>}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="notification-settings-schedule">
        <strong>{t("Schedule")}</strong>
        <label className="notification-settings-time-row">
          <input
            type="checkbox"
            checked={value.dndEnabled}
            onChange={() => onChange({ dndEnabled: !value.dndEnabled })}
          />
          <span
            className={`notification-settings-empty-radio ${
              value.dndEnabled ? "notification-settings-radio-active" : ""
            }`}
          />
          <span>{t("Do not notify me from")}</span>
          <input
            type="text"
            value={value.dndStartTime}
            onChange={(event) => onChange({ dndStartTime: event.target.value })}
          />
          <span>{t("to")}</span>
          <input
            type="text"
            value={value.dndEndTime}
            onChange={(event) => onChange({ dndEndTime: event.target.value })}
          />
        </label>
      </div>
      <div className="notification-settings-days">
        <span>{t("Do not disturb me on my days off")}</span>
        <div>
          {[
            t("Mon"),
            t("Tue"),
            t("Wed"),
            t("Thu"),
            t("Fri"),
            t("Sat"),
            t("Sun"),
          ].map((day) => {
            const isSelected = value.dndDays.includes(day);

            return (
              <button
                className={isSelected ? "notification-settings-day-active" : ""}
                type="button"
                key={day}
                onClick={() => toggleDay(day)}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const NotificationRow = ({
  item,
  isActionsActive,
  onClick,
  onBookmarkToggle,
  onArchiveToggle,
  onUnarchive,
  activeTab,
}) => {
  const isUnread = !item.isSeen;
  const isArchived = Boolean(item.isArchived);
  const isBookmarked = Boolean(item.isBookmarked);
  const archiveActionLabel =
    activeTab === "Archive" || isArchived ? "Restore" : "Archive";
  const archiveActionHandler =
    activeTab === "Archive" || isArchived ? onUnarchive : onArchiveToggle;
  const bookmarkActionLabel = isBookmarked ? "Unbookmark" : "Bookmark";

  return (
    <article
      className={`notification-row ${
        isActionsActive ? "notification-row-actions-active" : ""
      } ${isUnread ? "notification-row-unread" : ""}`}
      onClick={onClick}
    >
      <div className="notification-status">
        {isUnread && <span className="notification-unread-dot" />}
        {item.isCompleted ? (
          <span className="notification-complete-icon" aria-hidden="true" />
        ) : (
          <span className="notification-check-ring" />
        )}
      </div>

      <div className="notification-main">
        <div className="notification-title-line">
          <div className="notification-title-text">
            <h2>{item.title}</h2>
            <p>{item.projectName}</p>
          </div>
          {item.commentsCount > 0 && (
            <div className="notification-comments">
              <span>{item.commentsCount}</span>
              <img src={commentIcon} alt="" />
            </div>
          )}
          <div
            className="notification-actions"
            onClick={(event) => event.stopPropagation()}
          >
            <button type="button" aria-label="Open message" onClick={onClick}>
              <img src={mailIcon} alt="" />
            </button>
            <button
              type="button"
              aria-label={bookmarkActionLabel}
              onClick={onBookmarkToggle}
            >
              <img src={isBookmarked ? bookmarkFilled : bookmarkIcon} alt="" />
            </button>
            <button
              type="button"
              aria-label={archiveActionLabel}
              onClick={archiveActionHandler}
            >
              <img src={isArchived ? deleteFilled : deleteIcon} alt="" />
            </button>
          </div>
        </div>

        <div className="notification-meta">
          {item?.senderPicture ? (
            <img
              className="notification-avatar"
              src={
                resolveNotificationAvatarUrl(item.senderPicture) || userAvatar
              }
              alt=""
              onError={(event) => {
                event.currentTarget.src = userAvatar;
              }}
            />
          ) : (
            <div className="initials">
              {item.title
                ?.split(" ")
                .map((word) => word[0])
                .join("")
                .substring(0, 2)
                .toUpperCase()}
            </div>
          )}

          <div>
            <div className="notification-person-line">
              <strong>{item.senderName}</strong>
              <span className="pointer">{item.actionText}</span>
              <span className="notification-dot-separator">|</span>
              <span>{item.relativeTime}</span>
            </div>
            {item.message && (
              <p className="notification-message">
                <strong>{item.assigneeName}</strong>
                {item.assigneeName ? ", " : ""}
                {item.message}
              </p>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

function buildDefaultEmailSelection() {
  const selection = {};
  emailNotificationGroups.forEach((group) => {
    selection[group.title] = true;
    group.items.forEach((item) => {
      selection[item] = true;
    });
  });
  return selection;
}

function normalizeNotificationList(list) {
  const notifications = Array.isArray(list) ? list : [];

  return notifications
    .map((item, index) => {
      const createdAt = getNotificationDate(item);
      const title = firstDefined([
        item?.title,
        item?.task,
        item?.notificationTitle,
        item?.subject,
        item?.name,
        item?.description,
        item?.text,
        "Notification",
      ]);
      const projectName = firstDefined([
        item?.projectName,
        item?.project,
        item?.projectTitle,
        item?.workspaceName,
        item?.moduleName,
        item?.description,
      ]);
      const senderName = firstDefined([
        item?.senderName,
        item?.person,
        item?.createdBy,
        item?.userName,
        item?.authorName,
        item?.title,
        "System",
      ]);
      const actionText = firstDefined([
        item?.action,
        item?.actionText,
        item?.notificationType,
        item?.type,
        "updated",
      ]);
      const message = firstDefined([
        item?.message,
        item?.body,
        item?.description,
        item?.text,
        item?.content,
        "",
      ]);

      return {
        ...item,
        id: firstDefined([
          item?.id,
          item?.notificationId,
          item?.notificationid,
          item?.notificationID,
          `${index}`,
        ]),
        createdAt,
        title: truncateText(toText(title), 120),
        projectName: truncateText(toText(projectName), 120),
        senderName: truncateText(toText(senderName), 80),
        actionText: truncateText(humanizeActionText(toText(actionText)), 80),
        message: truncateText(toText(message), 180),
        assigneeName: firstDefined([
          item?.assignee,
          item?.assigneeName,
          item?.recipientName,
          item?.targetName,
          "",
        ]),
        commentsCount: toNumber(
          firstDefined([
            item?.commentsCount,
            item?.comments,
            item?.commentCount,
            item?.totalComments,
            0,
          ]),
        ),
        isSeen: toBoolean(
          firstDefined([
            item?.IsSeen,
            item?.isSeen,
            item?.seen,
            item?.read,
            false,
          ]),
        ),
        isBookmarked: toBoolean(
          firstDefined([
            item?.IsBookmarked,
            item?.isBookmarked,
            item?.bookmarked,
            false,
          ]),
        ),
        isArchived: toBoolean(
          firstDefined([
            item?.IsArchived,
            item?.isArchived,
            item?.archived,
            false,
          ]),
        ),
        isCompleted: toBoolean(
          firstDefined([
            item?.isCompleted,
            item?.completed,
            item?.isDone,
            false,
          ]),
        ),
        senderPicture: resolveNotificationAvatarUrl(
          firstDefined([
            item?.senderPicture,
            item?.avatar,
            item?.profilePicture,
            item?.userImage,
            item?.userimageurl,
            "",
          ]),
        ),
        relativeTime: createdAt
          ? moment(createdAt).fromNow()
          : firstDefined([item?.time, item?.createdAtText, ""]),
      };
    })
    .sort((left, right) => {
      const leftDate = left.createdAt ? new Date(left.createdAt).getTime() : 0;
      const rightDate = right.createdAt
        ? new Date(right.createdAt).getTime()
        : 0;
      return rightDate - leftDate;
    });
}

function groupNotificationsByDate(notifications) {
  const groups = [];
  const groupMap = new Map();

  notifications.forEach((item) => {
    const label = getDateBucketLabel(item.createdAt);
    if (!groupMap.has(label)) {
      groupMap.set(label, []);
      groups.push({ label, items: groupMap.get(label) });
    }
    groupMap.get(label).push(item);
  });

  return groups;
}

function getDateBucketLabel(dateValue) {
  if (!dateValue) {
    return "OLDER";
  }

  const normalized = moment(dateValue).utc().local().startOf("day");
  const today = moment().local().startOf("day");
  const diffDays = today.diff(normalized, "days");

  if (diffDays === 0) {
    return "TODAY";
  }

  if (diffDays === 1) {
    return "YESTERDAY";
  }

  return normalized.format("MMM D, YYYY");
}

function getNotificationDate(item) {
  const dateCandidates = [
    item?.createdAt,
    item?.createdOn,
    item?.createdDate,
    item?.notificationDate,
    item?.date,
    item?.timestamp,
    item?.sentAt,
  ];

  const dateCandidate = dateCandidates.find(Boolean);
  if (!dateCandidate) {
    return null;
  }

  const parsed = moment.utc(dateCandidate);
  return parsed.isValid() ? parsed.toDate() : null;
}

function normalizeSettingsResponse(responseData) {
  const source = Array.isArray(responseData)
    ? responseData[0] || {}
    : responseData?.data || responseData || {};

  const browserModeValue = firstDefined([
    source.browserMode,
    source.browserNotificationType,
    source.browserNotifications,
    source.browserSetting,
    source.BrowserMode,
  ]);
  const browserModeFallback =
    source.browserEnabled === false || source.BrowserEnabled === false
      ? "Nothing"
      : DEFAULT_SETTINGS_DRAFT.browserMode;
  const parsedDndDays = Array.isArray(source.dndDays)
    ? source.dndDays
    : Array.isArray(source.DndDays)
      ? source.DndDays
      : Array.isArray(source.daysOff)
        ? source.daysOff
        : Array.isArray(source.DaysOff)
          ? source.DaysOff
          : Array.isArray(source.quietDays)
            ? source.quietDays
            : parseNotificationSelectionList(
                firstDefined([
                  source.dndDaysJson,
                  source.DoNotDisturbDaysJson,
                  source.daysOffJson,
                  source.DaysOffJson,
                  source.quietDaysJson,
                  "",
                ]),
              );

  const draft = {
    browserMode: toText(browserModeValue, browserModeFallback),
    preferredEmail: toText(
      firstDefined([
        source.preferredEmail,
        source.PreferredEmail,
        source.email,
        source.notificationEmail,
        window.localStorage.getItem("userEmail"),
        "",
      ]),
      "",
    ),
    dndEnabled: toBoolean(
      firstDefined([
        source.dndEnabled,
        source.doNotDisturb,
        source.doNotDisturbEnabled,
        source.DoNotDisturbEnabled,
        source.quietHoursEnabled,
        false,
      ]),
    ),
    dndPauseOption: toText(
      firstDefined([
        source.dndPauseOption,
        source.DoNotDisturbPauseOption,
        source.pauseOption,
        source.pauseNotificationsFor,
        "",
      ]),
      "",
    ),
    dndStartTime: toText(
      firstDefined([
        source.dndStartTime,
        source.DndStartTime,
        source.quietHoursStart,
        source.QuietHoursStart,
        source.quietStartTime,
        source.startTime,
        DEFAULT_SETTINGS_DRAFT.dndStartTime,
      ]),
      DEFAULT_SETTINGS_DRAFT.dndStartTime,
    ),
    dndEndTime: toText(
      firstDefined([
        source.dndEndTime,
        source.DndEndTime,
        source.quietHoursEnd,
        source.QuietHoursEnd,
        source.quietEndTime,
        source.endTime,
        DEFAULT_SETTINGS_DRAFT.dndEndTime,
      ]),
      DEFAULT_SETTINGS_DRAFT.dndEndTime,
    ),
    dndDays:
      parsedDndDays.length > 0 ? parsedDndDays : DEFAULT_SETTINGS_DRAFT.dndDays,
    emailSelection: normalizeEmailSelection(source),
  };

  return {
    raw: source,
    draft,
  };
}

function normalizeEmailSelection(source) {
  const selection = buildDefaultEmailSelection();
  const enabledItems = parseNotificationSelectionList(
    firstDefined([
      source.emailSelections,
      source.EmailSelections,
      source.emailSelection,
      source.EmailSelection,
      source.emailNotificationSelections,
      source.emailNotificationItems,
      source.enabledEmailItems,
      source.EmailSelectionsJson,
      [],
    ]),
  );

  Object.keys(selection).forEach((key) => {
    selection[key] = enabledItems.includes(key);
  });

  return selection;
}

function buildSettingsPayload(source, draft) {
  const basePayload =
    source && typeof source === "object" && !Array.isArray(source)
      ? { ...source }
      : {};

  const enabledEmailItems = Object.keys(draft.emailSelection || {}).filter(
    (key) => draft.emailSelection[key],
  );
  const emailGroupStates = buildEmailGroupStates(draft.emailSelection || {});

  return {
    ...basePayload,
    browserEnabled: draft.browserMode !== "Nothing",
    BrowserEnabled: draft.browserMode !== "Nothing",
    browserMode: draft.browserMode,
    BrowserMode: draft.browserMode,
    browserNotificationType: draft.browserMode,
    preferredEmail: draft.preferredEmail,
    PreferredEmail: draft.preferredEmail,
    email: draft.preferredEmail,
    emailEnabled: enabledEmailItems.length > 0,
    EmailEnabled: enabledEmailItems.length > 0,
    emailSelections: enabledEmailItems,
    EmailSelections: enabledEmailItems,
    emailSelectionsJson: JSON.stringify(enabledEmailItems),
    EmailSelectionsJson: JSON.stringify(enabledEmailItems),
    emailNotificationSelections: enabledEmailItems,
    WorkUpdatesEnabled: emailGroupStates.WorkUpdatesEnabled,
    CommunicationEnabled: emailGroupStates.CommunicationEnabled,
    SummariesEnabled: emailGroupStates.SummariesEnabled,
    OtherEnabled: emailGroupStates.OtherEnabled,
    dndEnabled: draft.dndEnabled,
    doNotDisturbEnabled: draft.dndEnabled,
    DoNotDisturbEnabled: draft.dndEnabled,
    pauseNotificationsFor: draft.dndPauseOption,
    dndPauseOption: draft.dndPauseOption,
    DoNotDisturbPauseOption: draft.dndPauseOption,
    dndStartTime: draft.dndStartTime,
    DndStartTime: draft.dndStartTime,
    dndEndTime: draft.dndEndTime,
    DndEndTime: draft.dndEndTime,
    QuietHoursStart: draft.dndStartTime,
    QuietHoursEnd: draft.dndEndTime,
    dndDays: draft.dndDays,
    DndDays: draft.dndDays,
    dndDaysJson: JSON.stringify(draft.dndDays),
    DoNotDisturbDaysJson: JSON.stringify(draft.dndDays),
    daysOff: draft.dndDays,
  };
}

function parseNotificationSelectionList(value) {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch {
      return trimmed
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return [];
}

function buildEmailGroupStates(emailSelection) {
  return emailNotificationGroups.reduce((accumulator, group) => {
    const isEnabled = group.items.some((item) => Boolean(emailSelection[item]));
    const key =
      group.title === "Updates on your work"
        ? "WorkUpdatesEnabled"
        : group.title === "Communication"
          ? "CommunicationEnabled"
          : group.title === "Summaries"
            ? "SummariesEnabled"
            : "OtherEnabled";

    accumulator[key] = isEnabled;
    return accumulator;
  }, {});
}

function buildNotificationFilterRequest(activeFilter) {
  if (!activeFilter || !activeFilter.key || activeFilter.key === "all") {
    return {};
  }

  if (activeFilter.key === "fromperson" || activeFilter.key === "fromproject") {
    return {
      filter: activeFilter.key,
      filterId: activeFilter.value || "",
    };
  }

  return {
    filter: activeFilter.key,
  };
}

function buildFilterCatalog(notifications) {
  const personMap = new Map();
  const projectMap = new Map();

  (Array.isArray(notifications) ? notifications : []).forEach((item) => {
    const senderId = firstDefined([
      item?.senderUserId,
      item?.SenderUserId,
      item?.senderId,
    ]);
    const senderLabel = firstDefined([
      item?.senderName,
      item?.title,
      item?.createdBy,
      "System",
    ]);

    if (senderId && !personMap.has(senderId)) {
      personMap.set(senderId, {
        id: senderId,
        label: truncateText(toText(senderLabel), 60),
      });
    }

    const projectId = firstDefined([item?.projectId, item?.ProjectId]);
    const projectLabel = firstDefined([
      item?.projectName,
      item?.projectTitle,
      item?.workspaceName,
    ]);

    if (projectId && !projectMap.has(projectId)) {
      projectMap.set(projectId, {
        id: projectId,
        label: truncateText(toText(projectLabel || "Project"), 60),
      });
    }
  });

  return {
    personOptions: Array.from(personMap.values()),
    projectOptions: Array.from(projectMap.values()),
  };
}

function humanizeActionText(value) {
  if (!value) {
    return "";
  }

  return String(value).replace(/_/g, " ").replace(/\s+/g, " ").trim();
}

function firstDefined(values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return "";
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toBoolean(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return ["true", "1", "yes", "y"].includes(value.toLowerCase());
  }

  return Boolean(value);
}

function toText(value, fallback = "") {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number") {
    return String(value);
  }
  return fallback;
}

function safeParseNotificationData(value) {
  if (!value) {
    return null;
  }

  if (typeof value === "object") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function truncateText(value, maxLength) {
  const text = value ? String(value) : "";
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 3)}...`;
}

export default Notifications;
